import { AgGridReact } from "ag-grid-react";
import { Button } from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { DATE_FORMAT_YYYYMMDD, DateUtils } from "utils/dateutils";
import { ProjectContext } from "context/ProjectProvider";
import { getScheduleLookupReport } from "services/auth";
import CiqGridViewWrapper from "components/ciq-grid-view-wrapper";
import {
  getScheduleFileExtension,
  matchCustomUserProjectRole
} from "utils/utils";
import { ECustomUserTypes } from "constants/index";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { GET_SCHEDULE_VERSIONS } from "services/graphQL/ciq-queries";
import { useHistory, useParams } from "react-router";
import { dummyColumnDef2 } from "components/cell-renders";
import { TActivityTask } from "./models";
import { TDateRangeActivity } from "../../components/activities-filter-form";
import { DetailCellRendererActivity } from "./detail-cell-renderer";
import { activityColDefs } from "./col-defs/activity-col-def";
import { exportActivityReportAsCSV } from "./utils/export-activity-report-csv";
import { customSearchForActivityReport } from "./utils/custom-search";

const expandNodesInDetailGrid = (
  gridInfo: { api?: { forEachNode: (cb: (node: any) => void) => void } },
  isExpand: boolean
) => {
  gridInfo.api?.forEachNode((node) => {
    node.setExpanded(isExpand);
  });
};

const expandAllDetailGridNodes = (
  api: {
    forEachDetailGridInfo?: (
      cb: (gridInfo: {
        api?: { forEachNode: (cb: (node: any) => void) => void };
      }) => void
    ) => void;
  },
  isExpand: boolean
) => {
  api?.forEachDetailGridInfo?.((gridInfo: any) => {
    expandNodesInDetailGrid(gridInfo, isExpand);
  });
};
import { translateWithParentInfo } from "./utils/translate-with-parent-info";
import useAuditEventLog from "entity-app/utils/export-audit-log";

function ScheduleActivitiesReportComponent({
  openFilter,
  reportFilter
}: {
  openFilter: () => void;
  reportFilter: TDateRangeActivity | undefined;
}) {
  const history = useHistory();

  const { projectId } = useParams() as any;
  const gridRef = useRef<AgGridReact<TActivityTask>>(null);
  const {
    tokenRetrievalState,
    projectDetails,
    tokenContents,
    gqlClientForProject,
    isProjectArchived
  } = useContext(ProjectContext);
  const { saveAuditLogEvent } = useAuditEventLog(gqlClientForProject);
  const [activities, setActivities] = useState<Array<TActivityTask>>();
  const [searchText, setSearchText] = useState("");
  const [loadingExport, setLoadingExport] = useState(false);
  const [expandedRowCount, setExpandedRowCount] = useState(0);
  const isGCUser = matchCustomUserProjectRole(
    ECustomUserTypes.AllGCUserRole,
    tokenContents?.role
  );

  const { data: scheduleVersions } = useCIQQuery(GET_SCHEDULE_VERSIONS, {
    client: gqlClientForProject,
    fetchPolicy: "no-cache"
  });

  const activeSchedule = useMemo(() => {
    const selectedScheduleObj = scheduleVersions?.schedule_import_details?.find(
      (e: any) => e?.is_active
    );
    return selectedScheduleObj;
  }, [scheduleVersions]);

  const isCSVFile = useMemo(() => {
    return getScheduleFileExtension(activeSchedule, "csv");
  }, [activeSchedule]);

  useEffect(() => {
    const updateReportData = async () => {
      const response = await getScheduleLookupReport(
        tokenRetrievalState.token,
        {
          start_date: reportFilter?.startDate
            ? DateUtils.format(reportFilter?.startDate, DATE_FORMAT_YYYYMMDD)
            : null,
          end_date: reportFilter?.endDate
            ? DateUtils.format(reportFilter?.endDate, DATE_FORMAT_YYYYMMDD)
            : null
        }
      );
      if (response.data && response.data.success) {
        const data = response.data.success as Array<TActivityTask>;
        setActivities(translateWithParentInfo(data));
      } else {
        setActivities([]);
      }
    };
    updateReportData();
  }, [
    reportFilter?.endDate,
    reportFilter?.startDate,
    tokenRetrievalState.token
  ]);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api)
      customSearchForActivityReport(searchText, gridRef.current.api);
  }, [searchText]);

  const expandCollapseAll = useCallback(() => {
    const isExpand = expandedRowCount !== activities?.length; // tmpExpandedItemIds.length > 0;

    gridRef?.current?.api?.forEachNode((node) => {
      node.setExpanded(isExpand);
    });

    setTimeout(() => {
      if (isExpand && gridRef?.current?.api) {
        expandAllDetailGridNodes(gridRef.current.api, isExpand);
      }
    }, 100);
    if (isExpand) setExpandedRowCount(activities?.length || 0);
    else setExpandedRowCount(0);
    gridRef?.current?.api?.onGroupExpandedOrCollapsed();
  }, [activities?.length, expandedRowCount]);

  const onUpdateExpandedRow = () => {
    let count = 0;
    gridRef.current?.api?.forEachNode((node) => {
      if (node.expanded) count = 1 + count;
    });
    gridRef.current?.api?.forEachDetailGridInfo((gridInfo) => {
      gridInfo.api?.forEachNode((node) => {
        if (node.expanded) count = 1 + count;
      });
    });
    setExpandedRowCount(count);
  };

  const headerItems = [
    <CiqAgSearch
      key="ScheduleActivitiesReportComponent_search"
      gridRef={gridRef}
      placeholder="Search Activities"
      data-testid="schedule-activities-search"
      onSearch={(text) => {
        setSearchText(text);
      }}
    />,

    <Button
      key="ScheduleActivitiesReportComponentt_ExpandCollapse"
      onClick={() => {
        expandCollapseAll();
      }}
      className="w-[110px]"
      disabled={!activities || !activities.length}
      data-testid="schedule-activities-expand-toggle"
    >
      {expandedRowCount === activities?.length ? (
        <span>Collapse All</span>
      ) : (
        <span>Expand All</span>
      )}
    </Button>,
    <Button
      key="ScheduleActivitiesReportComponent_exportCSV"
      disabled={
        loadingExport ||
        !gridRef.current ||
        !projectDetails ||
        !!isProjectArchived
      }
      loading={loadingExport}
      data-testid="schedule-activities-export-csv"
      onClick={() => {
        if (gridRef.current) {
          setLoadingExport(true);
          gridRef?.current?.api?.forEachNode((node) => {
            node.setExpanded(true);
          });

          setTimeout(() => {
            try {
              if (gridRef.current && projectDetails) {
                const filename = `Schedule Lookahead Report_${projectDetails.name}.csv`;
                exportActivityReportAsCSV(
                  gridRef.current,
                  projectDetails,
                  filename
                );
                saveAuditLogEvent({
                  eventType: "SCHEDULE_LOOK_AHEAD_REPORT_EXPORT_CSV",
                  featureType: "ACTIVITIES",
                  filename
                });
              }
            } finally {
              setLoadingExport(false);
            }
          }, 3000);
        }
      }}
    >
      Export CSV
    </Button>,
    <Button
      key="ScheduleActivitiesReportComponentt_new"
      type="primary"
      onClick={openFilter}
      data-testid="schedule-activities-run-new"
    >
      Run New Report
    </Button>
  ];

  const colDefs = useMemo(() => {
    if (!isGCUser || isCSVFile) {
      return activityColDefs?.filter(
        (col: any) => col?.colId !== "critical" && col?.colId !== "total_slack"
      );
    }
    return [dummyColumnDef2, ...activityColDefs];
  }, [isGCUser, isCSVFile]);

  const gotoReports = () => {
    history.push(`/project/${projectId}/reports/project-reports`);
  };

  return (
    <CiqGridViewWrapper
      headerInfo={{
        entityName: "Schedule Lookahead Report",
        items: headerItems,
        backButton: gotoReports
      }}
      gridInfo={{
        gridRef,
        columnDefs: colDefs,
        rowData: activities,
        masterDetail: true,
        detailCellRenderer: DetailCellRendererActivity,
        detailRowAutoHeight: true,
        sideBar: null,
        suppressMovableColumns: true,
        rowSelection: {
          mode: "singleRow",
          checkboxes: false
        },
        context: { projectDetails },
        onRowGroupOpened: (event) => {
          if (event.expanded && gridRef.current && gridRef.current.api)
            customSearchForActivityReport(searchText, gridRef.current.api);
          onUpdateExpandedRow();
        }
      }}
    />
  );
}
export default ScheduleActivitiesReportComponent;
